// Invitation manager modal — used by founder from the chat page.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { UserPlus, Copy, X, Mail, CheckCircle2, Clock, Ban } from "lucide-react";

import { API, getToken } from "../api";

const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function InviteManagerModal({ caseId, onClose }) {
  const [list, setList] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ invitee_email: "", invitee_mobile: "",
                                       role: "DIRECTOR_AND_SHAREHOLDER", share_percent: "",
                                       display_name: "" });
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const r = await client.get(`/cases/${caseId}/invitations`);
      setList(r.data);
    } catch (e) {
      setList([]);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [caseId]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await client.post(`/cases/${caseId}/invitations`, {
        invitee_email: form.invitee_email.trim().toLowerCase(),
        invitee_mobile: form.invitee_mobile.trim(),
        role: form.role,
        share_percent: form.share_percent ? Number(form.share_percent) : null,
        display_name: form.display_name || null,
      });
      toast.success(`Invitation sent to ${form.invitee_email}`);
      navigator.clipboard?.writeText(r.data.invite_link).catch(() => {});
      setShowForm(false);
      setForm({ invitee_email: "", invitee_mobile: "", role: "DIRECTOR_AND_SHAREHOLDER",
                share_percent: "", display_name: "" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invitation");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(token) {
    if (!window.confirm("Revoke this invitation? The co-founder will not be able to use the link anymore.")) return;
    try {
      await client.post(`/invitations/${token}/revoke`);
      toast.success("Invitation revoked");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="invite-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display text-lg font-semibold flex items-center gap-2">
            <UserPlus size={18} /> Co-founder invitations
          </div>
          <button onClick={onClose} data-testid="invite-modal-close"
                  className="text-slate-500 hover:text-slate-900">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {!showForm && (
            <button onClick={() => setShowForm(true)}
                    data-testid="open-invite-form-btn"
                    className="w-full mb-5 bg-[#C5283F] text-white rounded-lg py-3 font-medium hover:bg-[#A82035] inline-flex items-center justify-center gap-2">
              <UserPlus size={14} /> Invite a co-founder
            </button>
          )}

          {showForm && (
            <form onSubmit={submit} className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-5 space-y-3" data-testid="invite-form">
              <div className="text-sm font-medium text-slate-700 mb-2">
                They&apos;ll receive an email with a 6-digit code. The link expires in 72 hours.
              </div>
              <Field label="Co-founder's name" value={form.display_name}
                     onChange={(v) => setForm({ ...form, display_name: v })}
                     testid="inv-name" />
              <Field label="Email *" type="email" value={form.invitee_email}
                     onChange={(v) => setForm({ ...form, invitee_email: v })}
                     required testid="inv-email" />
              <Field label="Mobile *" value={form.invitee_mobile}
                     onChange={(v) => setForm({ ...form, invitee_mobile: v })}
                     required testid="inv-mobile" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold block mb-1.5">Role</label>
                  <select value={form.role}
                          onChange={(e) => setForm({ ...form, role: e.target.value })}
                          data-testid="inv-role"
                          className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm">
                    <option value="DIRECTOR">Director</option>
                    <option value="SHAREHOLDER">Shareholder</option>
                    <option value="DIRECTOR_AND_SHAREHOLDER">Director & Shareholder</option>
                  </select>
                </div>
                <Field label="Share %" type="number" value={form.share_percent}
                       onChange={(v) => setForm({ ...form, share_percent: v })}
                       testid="inv-share" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={busy}
                        data-testid="inv-submit-btn"
                        className="flex-1 bg-[#C5283F] text-white rounded-lg py-2.5 font-medium hover:bg-[#A82035] disabled:opacity-50">
                  {busy ? "Sending…" : "Send invitation"}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                        className="px-4 py-2.5 text-slate-600 rounded-lg border border-slate-300 hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Sent invitations
          </div>
          {list === null && <div className="text-sm text-slate-500">Loading…</div>}
          {list && list.length === 0 && (
            <div className="text-sm text-slate-400 italic py-4 text-center">No invitations yet.</div>
          )}
          {list && list.map((inv) => (
            <InvitationRow key={inv.token} inv={inv} onRevoke={() => revoke(inv.token)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function InvitationRow({ inv, onRevoke }) {
  const isActive = inv.status === "PENDING" || inv.status === "VERIFIED";
  const icon = inv.status === "VERIFIED" ? <CheckCircle2 size={14} className="text-emerald-600" />
              : inv.status === "PENDING" ? <Clock size={14} className="text-amber-600" />
              : <Ban size={14} className="text-slate-400" />;
  const inviteLink = `${window.location.origin}/invite/${inv.token}`;
  return (
    <div className="border border-slate-200 rounded-lg p-3 mb-2" data-testid={`invitation-row-${inv.token}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Mail size={14} className="text-slate-500 shrink-0" />
          <div className="text-sm text-slate-900 truncate">{inv.invitee_email}</div>
        </div>
        <div className="text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1 text-slate-600">
          {icon} {inv.status.toLowerCase()}
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-1 truncate">
        Expires {new Date(inv.expires_at).toLocaleString()}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success("Invite link copied"); }}
          className="text-xs px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1">
          <Copy size={11} /> Copy link
        </button>
        {isActive && (
          <button
            onClick={onRevoke}
            data-testid={`revoke-${inv.token}`}
            className="text-xs px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded">
            Revoke
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold block mb-1.5">
        {label}
      </label>
      <input type={type} value={value} required={required}
             onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5283F] focus:border-transparent" />
    </div>
  );
}
